// Mirrors apps/api-server/src/services/shared-storage-externalize-placement-template.ts
// so the console can show sample folders while the template is being edited.
// The batch stays the authority: it may still add "(n)" to a folder or file name.

export const PLACEMENT_BUILTIN_PLACEHOLDERS = [
  { name: "recordIdParent", label: "レコードグループ番号" },
  { name: "recordIdLower3", label: "レコードID下3桁" },
  { name: "recordId", label: "レコードID" },
  { name: "fieldCode", label: "フィールドコード" },
  { name: "subtableFieldCode", label: "サブテーブルコード" },
  { name: "fileName", label: "ファイル名" },
] as const;

export type PlacementTemplateScope = "root_field" | "subtable_row";

export type PlacementSampleTemplateField = {
  fieldCode: string;
  fieldType: string;
};

export type PlacementSampleRecord = {
  recordId: string;
  fieldValues: Record<string, string | null>;
  fileNames: Array<{ fieldBindingKey: string; fileName: string }>;
};

export type PlacementSampleTarget = {
  fieldBindingKey: string;
  fieldCode: string;
  subtableFieldCode: string | null;
};

export type PlacementSampleResult =
  | { ok: true; path: string; truncated: boolean }
  | { ok: false; reason: "template_invalid" | "field_not_found" };

const FOLDER_SEGMENT_MAX_CHARS = 60;
const WINDOWS_RESERVED_NAMES = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/iu;
const FORBIDDEN_SEGMENT_CHARACTERS = /[\u0000-\u001f\u007f/\\:*?"<>|]/gu;
const BUILTIN_NAMES = new Set<string>(
  PLACEMENT_BUILTIN_PLACEHOLDERS.map((item) => item.name).filter((name) => name !== "fileName"),
);

export const FIXED_SAMPLE_RECORD_ID = "1234";
export const FIXED_SAMPLE_FILE_NAME = "サンプル.pdf";

const FIXED_SAMPLE_VALUES: Record<string, string> = {
  SINGLE_LINE_TEXT: "サンプル文字列",
  NUMBER: "12345",
  CALC: "100",
  DATE: "2026-01-31",
  DROP_DOWN: "選択肢1",
  RADIO_BUTTON: "選択肢1",
  RECORD_NUMBER: FIXED_SAMPLE_RECORD_ID,
};

/** Sample built from fixed per-type values, used when the app has no records. */
export function fixedPlacementSampleRecord(
  templateFields: readonly PlacementSampleTemplateField[],
): PlacementSampleRecord {
  return {
    recordId: FIXED_SAMPLE_RECORD_ID,
    fieldValues: Object.fromEntries(
      templateFields.map((field) => [field.fieldCode, FIXED_SAMPLE_VALUES[field.fieldType] ?? "サンプル"]),
    ),
    fileNames: [],
  };
}

export function renderPlacementSample(input: {
  template: string;
  scope: PlacementTemplateScope;
  record: PlacementSampleRecord;
  target: PlacementSampleTarget;
  templateFields: readonly PlacementSampleTemplateField[];
}): PlacementSampleResult {
  const segments = input.template.split("/");
  if (!input.template.trim() || input.template.startsWith("/") || segments.at(-1) !== "{fileName}") {
    return { ok: false, reason: "template_invalid" };
  }
  const recordIdNumber = Number(input.record.recordId);
  const builtins: Record<string, string | null> = {
    recordIdParent: Math.floor(recordIdNumber / 1000).toString().padStart(3, "0"),
    recordIdLower3: (recordIdNumber % 1000).toString().padStart(3, "0"),
    recordId: String(recordIdNumber),
    fieldCode: input.target.fieldCode,
    subtableFieldCode: input.target.subtableFieldCode,
  };
  const knownFields = new Set(input.templateFields.map((field) => field.fieldCode));
  const folders: string[] = [];
  let truncated = false;
  for (const segment of segments.slice(0, -1)) {
    if (!segment) return { ok: false, reason: "template_invalid" };
    let raw = "";
    let rest = segment;
    while (rest) {
      const open = rest.indexOf("{");
      const close = rest.indexOf("}");
      if (close !== -1 && (open === -1 || close < open)) return { ok: false, reason: "template_invalid" };
      const literal = open === -1 ? rest : rest.slice(0, open);
      if (literal && literalInvalid(literal)) return { ok: false, reason: "template_invalid" };
      raw += literal;
      if (open === -1) break;
      const end = rest.indexOf("}", open);
      if (end === -1) return { ok: false, reason: "template_invalid" };
      const name = rest.slice(open + 1, end);
      if (name.startsWith("field:")) {
        const fieldCode = name.slice("field:".length);
        if (!knownFields.has(fieldCode)) return { ok: false, reason: "field_not_found" };
        raw += input.record.fieldValues[fieldCode] ?? "";
      } else if (BUILTIN_NAMES.has(name)) {
        const value = builtins[name];
        if (value === null || (name === "subtableFieldCode" && input.scope !== "subtable_row")) {
          return { ok: false, reason: "template_invalid" };
        }
        raw += value;
      } else {
        return { ok: false, reason: "template_invalid" };
      }
      rest = rest.slice(end + 1);
    }
    const normalized = normalizeFolderSegment(raw);
    truncated ||= normalized.truncated;
    folders.push(normalized.segment);
  }
  const fileName = input.record.fileNames
    .find((entry) => entry.fieldBindingKey === input.target.fieldBindingKey)?.fileName ?? FIXED_SAMPLE_FILE_NAME;
  return { ok: true, path: [...folders, fileName].join("/"), truncated };
}

function literalInvalid(literal: string) {
  const trimmed = literal.trim();
  return trimmed === "." || trimmed === ".." ||
    new RegExp(FORBIDDEN_SEGMENT_CHARACTERS.source, "u").test(literal);
}

function normalizeFolderSegment(raw: string) {
  let value = raw.normalize("NFC")
    .replace(/\r\n?|\n/gu, "_")
    .replace(FORBIDDEN_SEGMENT_CHARACTERS, "_")
    .trim()
    .replace(/[. ]+$/u, "");
  if (!value) value = "_";
  if (value.startsWith(".") || WINDOWS_RESERVED_NAMES.test(value)) value = `_${value}`;
  const characters = Array.from(value);
  if (characters.length <= FOLDER_SEGMENT_MAX_CHARS) return { segment: value, truncated: false };
  return {
    segment: characters.slice(0, FOLDER_SEGMENT_MAX_CHARS).join("").replace(/[. ]+$/u, "") || "_",
    truncated: true,
  };
}
